import React from "react";
import { Mail, Copy, Download, Loader } from "lucide-react";

const TONE_OPTIONS = [
  { id: "warm", label: "Warm", hint: "Conversational, human" },
  { id: "direct", label: "Direct", hint: "Concise, results-first" },
  { id: "executive", label: "Executive", hint: "Senior, polished" },
];

export default function CoverLetterTab({
  coverLetter,
  coverLoading,
  coverCopied,
  onRequestCover,
  onCopyCover,
  onDownloadCoverPdf,
  onEmailDraftCover,
}) {
  if (!coverLetter) return null;
  return (
    <div
      className="rm-card-static p-6 md:p-8 mb-8"
      data-testid="cover-letter-card"
      style={{ background: "#FFFBEB" }}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ background: "#FF6A00", color: "white", border: "3px solid #0F1B2D" }}
          >
            <Mail size={20} strokeWidth={2.5} />
          </div>
          <div>
            <div className="rm-label">Cover Letter</div>
            <h2 className="font-display text-2xl uppercase leading-tight">
              {coverLetter.tone_descriptor || "Tailored voice"}.
            </h2>
          </div>
        </div>
      </div>

      <div
        className="mb-4 p-3 rounded-md flex items-center gap-3 flex-wrap"
        style={{ background: "white", border: "2px dashed #0F1B2D" }}
        data-testid="cover-tone-row"
      >
        <span
          className="font-mono-rm text-[10px] uppercase tracking-widest"
          style={{ color: "#475569", fontWeight: 700 }}
        >
          Re-roll voice:
        </span>
        {TONE_OPTIONS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => onRequestCover(t.id)}
            disabled={coverLoading}
            title={t.hint}
            className="text-[11px] font-semibold px-2.5 py-1 transition-colors"
            style={{
              background: "white",
              color: "#0F1B2D",
              border: "2px solid #0F1B2D",
              borderRadius: 4,
              cursor: coverLoading ? "wait" : "pointer",
              opacity: coverLoading ? 0.5 : 1,
            }}
            data-testid={`cover-tone-${t.id}`}
          >
            {t.label}
          </button>
        ))}
        {coverLoading && (
          <span
            className="font-mono-rm text-[10px] uppercase tracking-widest flex items-center gap-1"
            style={{ color: "#FF6A00" }}
          >
            <Loader size={11} className="animate-spin" /> rewriting…
          </span>
        )}
      </div>

      {coverLetter.subject_line && (
        <div
          className="mb-4 p-3 rounded-md"
          style={{ background: "white", border: "3px solid #0F1B2D" }}
        >
          <div className="rm-label mb-1">Subject line</div>
          <div className="text-sm font-bold">{coverLetter.subject_line}</div>
        </div>
      )}

      <div
        className="space-y-3 text-sm whitespace-pre-line p-4 rounded-md mb-4"
        style={{ background: "white", border: "3px solid #0F1B2D" }}
        data-testid="cover-letter-body"
      >
        <p>{coverLetter.greeting}</p>
        <p>{coverLetter.hook}</p>
        <p>{coverLetter.body}</p>
        <p>{coverLetter.close}</p>
        <p>
          {coverLetter.sign_off}
          <br />
          {coverLetter.signature}
        </p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="cover-actions">
        <button
          onClick={onCopyCover}
          className="rm-btn rm-btn-primary text-xs px-3 py-2"
          data-testid="copy-cover-btn"
        >
          <Copy size={14} /> {coverCopied ? "Copied ✓" : "Copy"}
        </button>
        <button
          onClick={onDownloadCoverPdf}
          className="rm-btn rm-btn-primary text-xs px-3 py-2"
          style={{ background: "#0F1B2D", color: "#FFC145", borderColor: "#0F1B2D" }}
          data-testid="cover-download-pdf-btn"
        >
          <Download size={14} /> Download PDF
        </button>
        <button
          onClick={onEmailDraftCover}
          className="rm-btn rm-btn-ghost text-xs px-3 py-2"
          data-testid="cover-email-draft-btn"
        >
          <Mail size={14} /> Open in email
        </button>
      </div>
    </div>
  );
}
